import type { IWithNavigateProps } from '@frontend/components/elements/HoC/withNavigate';
import { withNavigate } from '@frontend/components/elements/HoC/withNavigate';
import type { EpisodeCategory } from '../../../../common/enums/episodeCategory';
import type { IPatient } from '../../../../common/interfaces/patient';
import type { IService } from '../../interfaces/service';
import type { ISubsequentService } from '../../interfaces/subsequentService';
import { getServiceLabel } from '../../../../common/customControls/getServiceLabel';
import { ServiceType } from '../../../../common/enums/serviceType';
import { AbortableFetch } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { Button, ButtonContainer, HeaderDivider } from '../../elements/common';
import { Component } from '../../elements/Component';

interface ICreateRequestActionsProps extends IWithNavigateProps {
    add?: { serviceId: string; withProcedure: boolean };
    createRequest: (serviceId: string, withProcedure: boolean) => void;
    episodeActions?: boolean;
    episodeCategory?: EpisodeCategory;
    hideActions?: boolean;
    lastServiceId?: string;
    onLoaded?: (hasServices: boolean) => void;
    patient: IPatient;
}

interface ICreateRequestActionsState {
    consultationServices: IService[];
    counselingServices: IService[];
    procedureRequestServices: IService[];
    referralServices: IService[];
    requestServices: IService[];
    showHeader: boolean;
    subsequentServices: ISubsequentService[];
}

class InnerRequestCreateActions extends Component<ICreateRequestActionsProps, ICreateRequestActionsState> {
    private abortableFetch = new AbortableFetch();

    constructor(props: ICreateRequestActionsProps, context?: any) {
        super(props, context);

        this.state = {
            consultationServices: [],
            referralServices: [],
            counselingServices: [],
            procedureRequestServices: [],
            requestServices: [],
            subsequentServices: [],
            showHeader: false,
        };
    }

    public async componentDidMount() {
        await this.load(this.props.lastServiceId);
    }

    public async componentDidUpdate(prevProps: Readonly<ICreateRequestActionsProps>) {
        if (this.props.lastServiceId !== prevProps.lastServiceId) {
            await this.load(this.props.lastServiceId);
        }
    }

    public componentWillUnmount() {
        this.abortableFetch.abort();
    }

    public render() {
        const buttons = [
            <>
                {this.state.procedureRequestServices &&
                    this.state.procedureRequestServices.map((item) => (
                        <Button
                            active={this.isActive(item.id, true)}
                            automation={`${item.code}-procedureRequestServices`}
                            careplan={!this.props.episodeActions}
                            color={this.props.episodeActions ? 'primary' : 'tertiary'}
                            icon={item.serviceType}
                            key={`${item.id}-procedureRequestServices`}
                            large
                            onClick={() => this.props.createRequest(item.id, true)}
                            outlined={this.episodeAction(item.id)}
                        >
                            {getServiceLabel(item.code, 'createProcedure', item.name)}
                        </Button>
                    ))}
                {this.state.requestServices &&
                    this.state.requestServices.map((item) => (
                        <Button
                            active={this.isActive(item.id, false)}
                            automation={`${item.code}-requestServices`}
                            careplan={!this.props.episodeActions}
                            color={this.props.episodeActions ? 'primary' : 'tertiary'}
                            icon={item.serviceType}
                            key={`${item.id}-requestServices`}
                            large
                            onClick={() => this.props.createRequest(item.id, false)}
                            outlined={this.episodeAction(item.id)}
                        >
                            {getServiceLabel(
                                item.code,
                                'createRequest',
                                `${item.name} (${translateKey('request.request')})`
                            )}
                        </Button>
                    ))}
                {this.state.referralServices &&
                    this.state.referralServices.map((item) => (
                        <Button
                            active={this.isActive(item.id, false)}
                            automation={`${item.code}-referralServices`}
                            careplan={!this.props.episodeActions}
                            color={this.props.episodeActions ? 'primary' : 'tertiary'}
                            icon={item.serviceType}
                            key={`${item.id}-referralServices`}
                            large
                            onClick={() => this.props.createRequest(item.id, false)}
                            outlined={this.episodeAction(item.id)}
                        >
                            {getServiceLabel(item.code, 'createRequest', item.name)}
                        </Button>
                    ))}
                {this.state.consultationServices &&
                    this.state.consultationServices.map((item) => (
                        <Button
                            active={this.isActive(item.id, false)}
                            automation={`${item.code}-consultationServices`}
                            careplan={!this.props.episodeActions}
                            color={this.props.episodeActions ? 'primary' : 'tertiary'}
                            icon={item.serviceType}
                            key={`${item.id}-consultationServices`}
                            large
                            onClick={() => this.props.createRequest(item.id, false)}
                            outlined={this.episodeAction(item.id)}
                        >
                            {getServiceLabel(item.code, 'createRequest', item.name)}
                        </Button>
                    ))}
                {this.state.counselingServices &&
                    this.state.counselingServices.map((item) => (
                        <Button
                            active={this.isActive(item.id, false)}
                            automation={`${item.code}-counselingServices`}
                            careplan={!this.props.episodeActions}
                            color={this.props.episodeActions ? 'primary' : 'tertiary'}
                            icon={item.serviceType}
                            key={`${item.id}-counselingServices`}
                            large
                            onClick={() => this.props.createRequest(item.id, false)}
                            outlined={this.episodeAction(item.id)}
                        >
                            {getServiceLabel(item.code, 'createRequest', item.name)}
                        </Button>
                    ))}
            </>,
        ];
        if (this.props.episodeActions) {
            buttons.push(
                <Button
                    automation="worklist"
                    color="secondary"
                    large
                    onClick={() => this.props.navigate('/requests')}
                    outlined
                    right
                >
                    {translateKey('common.tasks')}
                </Button>
            );
        }

        return (
            <>
                {!this.props.episodeActions && this.state.showHeader && (
                    <HeaderDivider text={translateKey('request.labResearchHeader')} />
                )}
                {!this.props.hideActions && (
                    <ButtonContainer
                        alignRight={false}
                        buttons={buttons}
                        hideButtons={
                            this.state.procedureRequestServices.length +
                                this.state.requestServices.length +
                                this.state.referralServices.length +
                                this.state.consultationServices.length +
                                this.state.counselingServices.length ===
                            0
                        }
                        wrapContent
                    />
                )}
            </>
        );
    }

    private isActive(serviceId: string, usesProcedure: boolean) {
        if (this.props.episodeActions && this.props.add) {
            if (this.props.add.serviceId === serviceId && usesProcedure === this.props.add.withProcedure) {
                return 'active';
            } else {
                return 'inactive';
            }
        }
    }

    private episodeAction(serviceId: string) {
        if (this.state.subsequentServices.length === 0) {
            return false;
        }

        return this.state.subsequentServices.find((item) => item.succeedingServiceId === serviceId) ? false : true;
    }

    private async load(lastServiceId?: string) {
        if (lastServiceId) {
            await this.loadSubsequentServices(lastServiceId);
        }
        await this.getProcedureServices();
        await this.getConsultationServices();
        await this.getReferralServices();
        await this.getReferralSmallServices();
        await this.getProcedureRequestServices();
        await this.getCounselingServices();
    }

    private async loadSubsequentServices(lastServiceId: string) {
        const url = `/api/subsequentServices?serviceId=${lastServiceId}`;

        const result = await this.abortableFetch.fetchJson(url, 'GET');
        await this.setState({ subsequentServices: result.json.items });
    }

    private async getProcedureRequestServices() {
        const url = `/api/servicesList?servicetype=${ServiceType.examination}${
            this.props.episodeCategory ? `&episodeCategory=${this.props.episodeCategory}` : '&hasNoEpisodeCategory=true'
        }&showConsumer=true&showRequestCreator=true`;

        const result = await this.abortableFetch.fetchJson(url, 'GET');
        if (result.json.items.length) {
            await this.setState({ requestServices: result.json.items, showHeader: true });
            if (this.props.onLoaded) {
                this.props.onLoaded(true);
            }
        }
    }

    private async getProcedureServices() {
        const url = `/api/servicesList?servicetype=${ServiceType.examination}${
            this.props.episodeCategory ? `&episodeCategory=${this.props.episodeCategory}` : '&hasNoEpisodeCategory=true'
        }&showBiometrician=true&showRequestCreator=true`;

        const result = await this.abortableFetch.fetchJson(url, 'GET');
        if (result.json.items.length) {
            await this.setState({ procedureRequestServices: result.json.items, showHeader: true });
            if (this.props.onLoaded) {
                this.props.onLoaded(true);
            }
        }
    }

    private async getReferralServices() {
        const url = `/api/servicesList?servicetype=${ServiceType.referral}${
            this.props.episodeCategory ? `&episodeCategory=${this.props.episodeCategory}` : '&hasNoEpisodeCategory=true'
        }&showConsumer=true`;

        const result = await this.abortableFetch.fetchJson(url, 'GET');
        if (result.json.items.length) {
            await this.setState({ referralServices: result.json.items, showHeader: true });
            if (this.props.onLoaded) {
                this.props.onLoaded(true);
            }
        }
    }

    private async getReferralSmallServices() {
        const url = `/api/servicesList?servicetype=${ServiceType.referralSmall}${
            this.props.episodeCategory ? `&episodeCategory=${this.props.episodeCategory}` : '&hasNoEpisodeCategory=true'
        }&showConsumer=true`;

        const result = await this.abortableFetch.fetchJson(url, 'GET');
        if (result.json.items.length) {
            await this.setState({ referralServices: result.json.items, showHeader: true });
            if (this.props.onLoaded) {
                this.props.onLoaded(true);
            }
        }
    }

    private async getConsultationServices() {
        const url = `/api/servicesList?servicetype=${ServiceType.consultation}${
            this.props.episodeCategory ? `&episodeCategory=${this.props.episodeCategory}` : '&hasNoEpisodeCategory=true'
        }&showConsumer=true`;

        const result = await this.abortableFetch.fetchJson(url, 'GET');
        if (result.json.items.length) {
            await this.setState({ consultationServices: result.json.items, showHeader: true });
            if (this.props.onLoaded) {
                this.props.onLoaded(true);
            }
        }
    }

    private async getCounselingServices() {
        const url = `/api/servicesList?servicetype=${ServiceType.counseling}${
            this.props.episodeCategory ? `&episodeCategory=${this.props.episodeCategory}` : '&hasNoEpisodeCategory=true'
        }&showConsumer=true`;

        const result = await this.abortableFetch.fetchJson(url, 'GET');
        if (result.json.items.length) {
            await this.setState({ counselingServices: result.json.items, showHeader: true });
            if (this.props.onLoaded) {
                this.props.onLoaded(true);
            }
        }
    }
}

export const RequestCreateActions = withNavigate(InnerRequestCreateActions);
